import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';
import {assertAndContinue} from 'Asserts';
import {MaybeRenderWithSignedURL} from 'external-files/SignedExtFileURLProxy';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import WarningIcon from 'ui-library/icons/WarningIcon';

export interface ExternalFileIFrameProps {
  scratchFile: GetAnalysisScratchFile;
  /**
   * Warning to display to the user above the iframe, informing them that they are viewing a
   * scratchFile defined externally to Benchling.
   */
  externalFileWarning: string;
  /**
   * This should only be used if:
   * - It is necessary for Javascript to run in the iframe, AND
   * - You have confirmed with Security setting this to true if you are rendering untrusted/user-generated
   *   HTML in the iframe.
   *
   * @default false
   */
  dangerouslyAllowScriptsInIFrame?: boolean;
  /**
   * Use this to override the ExternalFile resource's Content-Type header to `text/html` if its filename's
   * mimetype is inferred as text/html. Useful when the ExternalFile resource's Content-Type is not text/html,
   * but you want to render it in an iframe anyway. Otherwise, the scratchFile will be treated as an attachment and
   * downloaded, not rendered.
   *
   * @default false
   */
  dangerouslyOverrideHTMLFileContentTypeHeader?: boolean;
}

/**
 * Render an iframe of an ExternalFile, with a warning to the user that they are viewing a scratchFile external to
 * Benchling.
 *
 * @dangerous do not use elsewhere without approval from Security.
 * @package
 */
export default function ExternalFileIFrame({
  scratchFile,
  externalFileWarning,
  dangerouslyAllowScriptsInIFrame = false,
  dangerouslyOverrideHTMLFileContentTypeHeader = false,
}: ExternalFileIFrameProps): JSX.Element {
  return (
    <MaybeRenderWithSignedURL
      addHtmlContentTypeHeader={dangerouslyOverrideHTMLFileContentTypeHeader}
      externalFileId={scratchFile.externalFileId}
      url={scratchFile.downloadUrl!}
      renderWithSignedURL={(signedURL) => {
        if (signedURL === undefined) {
          return <CenteredHorizontalThrobber />;
        } else if (signedURL == null) {
          assertAndContinue(false, 'Got inaccessible scratchFile within an analysis', {
            externalFileId: scratchFile.externalFileId,
          });
          return <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading scratchFile." />;
        } else {
          return (
            <div className={css(styles.wrapper)}>
              <div className={css(styles.warning)}>
                <WarningIcon />
                <span>{externalFileWarning}</span>
              </div>
              {/* oxlint-disable-next-line iframe-has-title */}
              <iframe
                // Use an iframe sandbox to lock down the code executed in the user-defined HTML.
                // See https://www.w3schools.com/tags/att_iframe_sandbox.asp for a list of the restrictions.
                // The default is sandbox="", which applies all restrictions.
                sandbox={dangerouslyAllowScriptsInIFrame ? 'allow-scripts' : ''}
                src={signedURL}
                className={css(styles.iframe)}
                title={scratchFile.filename}
              />
            </div>
          );
        }
      }}
      owningEntityApiId={scratchFile.id}
    />
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  warning: {
    ...FONT_PROXIMA_13,
    background: GRAY_LIGHTER,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    padding: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  iframe: {
    flexGrow: 1,
    border: 'none',
  },
});
